import {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {Highlight, Text} from "@pg-design/text-module";
import {getPhoneDataFromString, priceFormat, priceM2Format} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {TurnstileAction} from "../../../app/constants/turnstile_actions";
import {createRpLeadPerformanceDebugTrace} from "../../../app/performance_debug";
import {prefetchTurnstileToken} from "../../../app/turnstile";
import {useSendMainLead} from "../../../application/hooks/use_send_main_lead";
import {setMainLeadData} from "../../../application/state/main_lead_slice";
import {getAmplitudeCtaLeadFormTrackingContext} from "../../../application/tracking/amplitude/get_amplitude_cta_lead_form_tracking_context";
import {IApplicationData} from "../../../application/types/IApplicationData";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {ReservationCtaPlaceholder} from "../../../reservation/components/ReservationCtaPlaceholder";
import {RESERVATION_PLACEMENT_CONFIG} from "../../../reservation/constants/reservation_placement_config";
import {usePropertyReservationState} from "../../../reservation/hooks/use_property_reservation_state";
import {trackReservationStart} from "../../../reservation/tracking/track_reservation_events";
import {IReservationRequest} from "../../../reservation/types/IReservationRequest";
import {useAppSelector} from "../../../store/hooks";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {trackAmplitudeViewModal} from "../../../tracking/amplitude/track_amplitude_view_modal";
import {getUserSourceDisplay} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../../user/api/patch_home_match_configuration";
import {getAmplitudeScreenLocation} from "../../../user/tracking/track_amplitude_phone_verified";
import {HomeMatchBasicPreferencesFlowSource} from "../../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {gtmLeadAfterRegistration} from "../../tracking/lead_after_registration_tracking";
import {PropertyInsightBox} from "./PropertyInsightBox";
import {useInsightVariant} from "./useInsightVariant";

interface IProps {
    isOpen: boolean;
    onConfirm: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentApplication?: IApplicationData | null) => void;
    onClose: () => void;
    onShowSimilar: () => void;
    // continues the flow with the reservation code - no lead is sent
    onReservationStart: (request: IReservationRequest) => void;
    overlayVariant?: IModalOverlayVariant;
}

const RESERVATION_CTA = RESERVATION_PLACEMENT_CONFIG.post_lead.cta;

export const LeadAfterRegistration = ({isOpen, onConfirm, onClose, onShowSimilar, onReservationStart, overlayVariant}: IProps) => {
    const dispatch = useDispatch();

    const flowState = useAppSelector((state) => state.registrationFlow);
    const viewType = useAppSelector((state) => state.viewType.current);
    const pageProperty = useAppSelector((state) => state.property.property);
    const isPageProperty = Boolean(pageProperty && pageProperty.id === flowState.propertyId);
    const {data: fetchedPropertyData, isLoading: isPropertyLoading} = useGetPropertyDetailQuery(flowState.propertyId as number, {
        skip: !isOpen || !flowState.propertyId || isPageProperty
    });
    const propertyData = isPageProperty ? pageProperty : fetchedPropertyData;
    const isLoading = !isPageProperty && isPropertyLoading;
    const {variant, buttonText: confirmButtonText, isLoading: isInsightLoading, percentageBelow, promotionExample} = useInsightVariant(propertyData);
    const [isSending, setIsSending] = useState(false);
    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const sendMainLead = useSendMainLead();
    const gtmCategory = flowState.gtmSourceSection ?? (flowState.userSource !== undefined ? getUserSourceDisplay(flowState.userSource) : "");
    // the same gate as the reservation buttons on the property page
    const {ctaState: reservationCtaState, request: reservationRequest} = usePropertyReservationState(isOpen ? (propertyData ?? null) : null, "post_lead");

    useEffect(() => {
        if (isOpen) {
            gtmLeadAfterRegistration("view", gtmCategory);
            trackAmplitudeViewModal({
                modalName: "post_price_reveal_modal",
                location: getAmplitudeScreenLocation(viewType),
                itemId: flowState.offerId ?? null,
                propertyId: flowState.propertyId ?? null
            });
            prefetchTurnstileToken(TurnstileAction.APPLICATION_CREATE);
        }
    }, [isOpen]);

    useEffect(() => {
        if (flowState?.vendorId && flowState?.offerId && flowState?.propertyId && flowState.leadSource && flowState.leadSourceSection) {
            dispatch(
                setMainLeadData({
                    vendorId: flowState.vendorId,
                    offerId: flowState.offerId,
                    propertyId: flowState.propertyId,
                    source: flowState.leadSource,
                    sourceSection: flowState.leadSourceSection,
                    trackingMeta: {openButtonSourceText: flowState.gtmClickText ?? null, openButtonSource: flowState.gtmSourceSection ?? null}
                })
            );
        }
    }, [
        flowState.vendorId,
        flowState.offerId,
        flowState.propertyId,
        flowState.leadSource,
        flowState.leadSourceSection,
        flowState.gtmClickText,
        flowState.gtmSourceSection
    ]);

    const handleConfirm = async () => {
        if (!userProfile) {
            return;
        }

        const trace = createRpLeadPerformanceDebugTrace("rp_lead_after_registration_confirm", {
            homeMatchActive: Boolean(homeMatchConfig?.is_active),
            offerId: flowState.offerId,
            propertyId: flowState.propertyId,
            vendorId: flowState.vendorId
        });

        setIsSending(true);
        trace.mark("submit_loader:enabled");
        gtmLeadAfterRegistration("click", gtmCategory, confirmButtonText);
        // This modal sends the lead directly (no lead modal open), so the amplitude context is not in
        // the mainLead store - build it here or the generate_lead event gate in sendLead stays closed
        const amplitudeLeadFormEventTracking =
            flowState.leadSource != null && flowState.leadSourceSection != null
                ? getAmplitudeCtaLeadFormTrackingContext({
                      source: flowState.leadSource,
                      sourceSection: flowState.leadSourceSection,
                      leadData: undefined,
                      ctaText: confirmButtonText,
                      offerId: flowState.offerId,
                      propertyId: flowState.propertyId,
                      viewType,
                      formLocation: "post_price_reveal_modal"
                  })
                : null;
        try {
            const application = await sendMainLead(
                {
                    name: userProfile.name ?? "",
                    email: userProfile.email ?? "",
                    phone: getPhoneDataFromString(userProfile.phone ?? ""),
                    marketing_email: userProfile.email_marketing_consent ?? false,
                    marketing_sms: userProfile.phone_marketing_consent ?? false
                },
                undefined,
                amplitudeLeadFormEventTracking ? {analyticsParams: {amplitudeLeadFormEventTracking}} : undefined
            );

            onConfirm(homeMatchConfig?.is_active ?? false, "afterLead", application ?? null);
        } finally {
            setIsSending(false);
            trace.end("submit_loader:disabled");
        }
    };

    const handleShowSimilar = () => {
        gtmLeadAfterRegistration("click", gtmCategory, "Pokaż podobne oferty");
        onShowSimilar();
    };

    const handleReservationStart = () => {
        if (!reservationRequest) {
            return;
        }

        gtmLeadAfterRegistration("click", gtmCategory, RESERVATION_CTA.label);
        trackReservationStart(reservationRequest, {authPlacement: flowState.amplitudePlacement});
        onReservationStart(reservationRequest);
    };

    const handleClose = () => {
        gtmLeadAfterRegistration("close", gtmCategory);
        onClose();
    };

    const handleKeepBrowsing = () => {
        gtmLeadAfterRegistration("click", gtmCategory, "Tylko się rozglądam");
        trackAmplitudeButtonClick({
            buttonName: "rp_post_price_reveal_browsing_only",
            buttonCategory: "rp_post_price_reveal",
            value: "just_browsing",
            itemId: flowState.offerId ?? null,
            propertyId: flowState.propertyId ?? null
        });
        onClose();
    };

    const isDisabled = !propertyData || isLoading || isSending || !userProfile;

    const renderSecondaryButton = () => {
        if (reservationCtaState === "pending") {
            // the slot stays empty until the availability is known, so the button under the cursor never changes its action
            return <ReservationCtaPlaceholder fullWidth className={buttonStyle} />;
        }

        if (reservationCtaState === "active") {
            return (
                <Button
                    variant="outlined_secondary"
                    fullWidth
                    disabled={isDisabled}
                    onClick={handleReservationStart}
                    className={buttonStyle}
                    dataTestId={RESERVATION_CTA.testId}
                >
                    {RESERVATION_CTA.label}
                </Button>
            );
        }

        return (
            <Button variant="outlined_secondary" fullWidth disabled={isDisabled} onClick={handleShowSimilar} className={buttonStyle}>
                Zobacz podobne w tej okolicy
            </Button>
        );
    };

    return (
        <Modal onModalClose={handleClose} isOpen={isOpen} shouldCloseOnOverlayClick={false} shouldCloseOnEsc={false} overlayVariant={overlayVariant}>
            <Modal.Layout.EmptyResponsive closeButtonVariant="none" className={modalStyle} dataTestId={OFFER_TEST_IDS.LEAD_FORM.PROPERTY_PRICE_REVEALED_MODAL}>
                <Text variant="headline_3" as="p" mb={1}>
                    <Highlight>Cena jest już widoczna!</Highlight>
                </Text>
                <Text variant="body_copy_2">
                    Utworzyliśmy dla Ciebie darmowe konto. Od teraz masz <strong>pełny dostęp do wszystkich cen na portalu.</strong>
                </Text>

                <div className={propertyBoxStyle}>
                    {isLoading && (
                        <div className={loaderWrapperStyle}>
                            <Loader />
                        </div>
                    )}
                    {propertyData && (
                        <>
                            <Image src={getThumbnailLink(propertyData.offer.main_image_url, 440, 248)} alt="" width="496px" height="240px" />
                            <div className={propertyInfoStyle}>
                                {propertyData.price && propertyData.price_m2 && (
                                    <div className={priceRowStyle}>
                                        <Text variant="headline_3" as="p">
                                            <Highlight>
                                                <strong>{priceFormat(propertyData.price)}</strong>
                                            </Highlight>
                                        </Text>
                                        <Text variant="info_txt_1">
                                            <strong>{priceM2Format(propertyData.price_m2)}</strong>
                                        </Text>
                                    </div>
                                )}
                                <div className={regionRowStyle}>
                                    <MapMarkerLocationIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />
                                    <Text variant="info_txt_1">{propertyData.offer.region.short_name}</Text>
                                </div>
                                <PropertyInsightBox
                                    variant={variant}
                                    isLoading={isInsightLoading}
                                    percentageBelow={percentageBelow}
                                    promotionExample={promotionExample}
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className={buttonsWrapper}>
                    <Button
                        variant="filled_primary"
                        fullWidth
                        disabled={isDisabled}
                        isLoading={isSending}
                        onClick={handleConfirm}
                        className={buttonStyle}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.CONFIRM_LEAD_BUTTON}
                    >
                        {confirmButtonText}
                    </Button>
                    {renderSecondaryButton()}
                    <Button variant="none_secondary" size="x-small" fullWidth onClick={handleKeepBrowsing} className={css({minH: "40px"})}>
                        Tylko się rozglądam
                    </Button>
                </div>
            </Modal.Layout.EmptyResponsive>
        </Modal>
    );
};

const modalStyle = css({
    position: "relative",
    flexDirection: "column",
    pt: 1.5,
    px: 1.5,
    pb: 0,

    md: {
        pt: 3,
        px: 3,
        maxWidth: "544px"
    }
});

const propertyBoxStyle = css({
    borderRadius: 2,
    boxShadow: "2",
    my: 3,
    w: "100%",
    maxWidth: "496px",
    mx: "auto",

    "& img": {
        maxWidth: "100%"
    }
});

const loaderWrapperStyle = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    p: 3
});

const propertyInfoStyle = css({
    p: 2
});

const priceRowStyle = css({
    alignItems: "baseline",
    display: "flex",
    gap: 1.5
});

const regionRowStyle = css({
    alignItems: "center",
    display: "flex",
    gap: 1.5,
    mb: 1.5
});

const buttonsWrapper = css({
    w: "100%",
    position: "sticky",
    bottom: 0,
    left: 0,
    backgroundColor: "var(--colors-white)",
    p: 1.5,
    md: {
        px: 3
    }
});

const buttonStyle = css({
    mb: 2,
    minH: "56px"
});
import {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {Highlight, Text} from "@pg-design/text-module";
import {getPhoneDataFromString, priceFormat, priceM2Format} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {TurnstileAction} from "../../../app/constants/turnstile_actions";
import {createRpLeadPerformanceDebugTrace} from "../../../app/performance_debug";
import {prefetchTurnstileToken} from "../../../app/turnstile";
import {useSendMainLead} from "../../../application/hooks/use_send_main_lead";
import {setMainLeadData} from "../../../application/state/main_lead_slice";
import {getAmplitudeCtaLeadFormTrackingContext} from "../../../application/tracking/amplitude/get_amplitude_cta_lead_form_tracking_context";
import {IApplicationData} from "../../../application/types/IApplicationData";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {ReservationCtaPlaceholder} from "../../../reservation/components/ReservationCtaPlaceholder";
import {RESERVATION_PLACEMENT_CONFIG} from "../../../reservation/constants/reservation_placement_config";
import {usePropertyReservationState} from "../../../reservation/hooks/use_property_reservation_state";
import {trackReservationStart} from "../../../reservation/tracking/track_reservation_events";
import {IReservationRequest} from "../../../reservation/types/IReservationRequest";
import {useAppSelector} from "../../../store/hooks";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {trackAmplitudeViewModal} from "../../../tracking/amplitude/track_amplitude_view_modal";
import {getUserSourceDisplay} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../../user/api/patch_home_match_configuration";
import {getAmplitudeScreenLocation} from "../../../user/tracking/track_amplitude_phone_verified";
import {HomeMatchBasicPreferencesFlowSource} from "../../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {gtmLeadAfterRegistration} from "../../tracking/lead_after_registration_tracking";
import {PropertyInsightBox} from "./PropertyInsightBox";
import {useInsightVariant} from "./useInsightVariant";

interface IProps {
    isOpen: boolean;
    onConfirm: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentApplication?: IApplicationData | null) => void;
    onClose: () => void;
    onShowSimilar: () => void;
    // continues the flow with the reservation code - no lead is sent
    onReservationStart: (request: IReservationRequest) => void;
    overlayVariant?: IModalOverlayVariant;
}

const RESERVATION_CTA = RESERVATION_PLACEMENT_CONFIG.post_lead.cta;

export const LeadAfterRegistration = ({isOpen, onConfirm, onClose, onShowSimilar, onReservationStart, overlayVariant}: IProps) => {
    const dispatch = useDispatch();

    const flowState = useAppSelector((state) => state.registrationFlow);
    const viewType = useAppSelector((state) => state.viewType.current);
    const pageProperty = useAppSelector((state) => state.property.property);
    const isPageProperty = Boolean(pageProperty && pageProperty.id === flowState.propertyId);
    const {data: fetchedPropertyData, isLoading: isPropertyLoading} = useGetPropertyDetailQuery(flowState.propertyId as number, {
        skip: !isOpen || !flowState.propertyId || isPageProperty
    });
    const propertyData = isPageProperty ? pageProperty : fetchedPropertyData;
    const isLoading = !isPageProperty && isPropertyLoading;
    const {variant, buttonText: confirmButtonText, isLoading: isInsightLoading, percentageBelow, promotionExample} = useInsightVariant(propertyData);
    const [isSending, setIsSending] = useState(false);
    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const sendMainLead = useSendMainLead();
    const gtmCategory = flowState.gtmSourceSection ?? (flowState.userSource !== undefined ? getUserSourceDisplay(flowState.userSource) : "");
    // the same gate as the reservation buttons on the property page
    const {ctaState: reservationCtaState, request: reservationRequest} = usePropertyReservationState(isOpen ? (propertyData ?? null) : null, "post_lead");

    useEffect(() => {
        if (isOpen) {
            gtmLeadAfterRegistration("view", gtmCategory);
            trackAmplitudeViewModal({
                modalName: "post_price_reveal_modal",
                location: getAmplitudeScreenLocation(viewType),
                itemId: flowState.offerId ?? null,
                propertyId: flowState.propertyId ?? null
            });
            prefetchTurnstileToken(TurnstileAction.APPLICATION_CREATE);
        }
    }, [isOpen]);

    useEffect(() => {
        if (flowState?.vendorId && flowState?.offerId && flowState?.propertyId && flowState.leadSource && flowState.leadSourceSection) {
            dispatch(
                setMainLeadData({
                    vendorId: flowState.vendorId,
                    offerId: flowState.offerId,
                    propertyId: flowState.propertyId,
                    source: flowState.leadSource,
                    sourceSection: flowState.leadSourceSection,
                    trackingMeta: {openButtonSourceText: flowState.gtmClickText ?? null, openButtonSource: flowState.gtmSourceSection ?? null}
                })
            );
        }
    }, [
        flowState.vendorId,
        flowState.offerId,
        flowState.propertyId,
        flowState.leadSource,
        flowState.leadSourceSection,
        flowState.gtmClickText,
        flowState.gtmSourceSection
    ]);

    const handleConfirm = async () => {
        if (!userProfile) {
            return;
        }

        const trace = createRpLeadPerformanceDebugTrace("rp_lead_after_registration_confirm", {
            homeMatchActive: Boolean(homeMatchConfig?.is_active),
            offerId: flowState.offerId,
            propertyId: flowState.propertyId,
            vendorId: flowState.vendorId
        });

        setIsSending(true);
        trace.mark("submit_loader:enabled");
        gtmLeadAfterRegistration("click", gtmCategory, confirmButtonText);
        // This modal sends the lead directly (no lead modal open), so the amplitude context is not in
        // the mainLead store - build it here or the generate_lead event gate in sendLead stays closed
        const amplitudeLeadFormEventTracking =
            flowState.leadSource != null && flowState.leadSourceSection != null
                ? getAmplitudeCtaLeadFormTrackingContext({
                      source: flowState.leadSource,
                      sourceSection: flowState.leadSourceSection,
                      leadData: undefined,
                      ctaText: confirmButtonText,
                      offerId: flowState.offerId,
                      propertyId: flowState.propertyId,
                      viewType,
                      formLocation: "post_price_reveal_modal"
                  })
                : null;
        try {
            const application = await sendMainLead(
                {
                    name: userProfile.name ?? "",
                    email: userProfile.email ?? "",
                    phone: getPhoneDataFromString(userProfile.phone ?? ""),
                    marketing_email: userProfile.email_marketing_consent ?? false,
                    marketing_sms: userProfile.phone_marketing_consent ?? false
                },
                undefined,
                amplitudeLeadFormEventTracking ? {analyticsParams: {amplitudeLeadFormEventTracking}} : undefined
            );

            onConfirm(homeMatchConfig?.is_active ?? false, "afterLead", application ?? null);
        } finally {
            setIsSending(false);
            trace.end("submit_loader:disabled");
        }
    };

    const handleShowSimilar = () => {
        gtmLeadAfterRegistration("click", gtmCategory, "Pokaż podobne oferty");
        onShowSimilar();
    };

    const handleReservationStart = () => {
        if (!reservationRequest) {
            return;
        }

        gtmLeadAfterRegistration("click", gtmCategory, RESERVATION_CTA.label);
        trackReservationStart(reservationRequest, {authPlacement: flowState.amplitudePlacement});
        onReservationStart(reservationRequest);
    };

    const handleClose = () => {
        gtmLeadAfterRegistration("close", gtmCategory);
        onClose();
    };

    const handleKeepBrowsing = () => {
        gtmLeadAfterRegistration("click", gtmCategory, "Tylko się rozglądam");
        trackAmplitudeButtonClick({
            buttonName: "rp_post_price_reveal_browsing_only",
            buttonCategory: "rp_post_price_reveal",
            value: "just_browsing",
            itemId: flowState.offerId ?? null,
            propertyId: flowState.propertyId ?? null
        });
        onClose();
    };

    const isDisabled = !propertyData || isLoading || isSending || !userProfile;

    const renderSecondaryButton = () => {
        if (reservationCtaState === "pending") {
            // the slot stays empty until the availability is known, so the button under the cursor never changes its action
            return <ReservationCtaPlaceholder fullWidth className={buttonStyle} />;
        }

        if (reservationCtaState === "active") {
            return (
                <Button
                    variant="outlined_secondary"
                    fullWidth
                    disabled={isDisabled}
                    onClick={handleReservationStart}
                    className={buttonStyle}
                    dataTestId={RESERVATION_CTA.testId}
                >
                    {RESERVATION_CTA.label}
                </Button>
            );
        }

        return (
            <Button variant="outlined_secondary" fullWidth disabled={isDisabled} onClick={handleShowSimilar} className={buttonStyle}>
                Zobacz podobne w tej okolicy
            </Button>
        );
    };

    return (
        <Modal onModalClose={handleClose} isOpen={isOpen} shouldCloseOnOverlayClick={false} shouldCloseOnEsc={false} overlayVariant={overlayVariant}>
            <Modal.Layout.EmptyResponsive closeButtonVariant="none" className={modalStyle} dataTestId={OFFER_TEST_IDS.LEAD_FORM.PROPERTY_PRICE_REVEALED_MODAL}>
                <Text variant="headline_3" as="p" mb={1}>
                    <Highlight>Cena jest już widoczna!</Highlight>
                </Text>
                <Text variant="body_copy_2">
                    Utworzyliśmy dla Ciebie darmowe konto. Od teraz masz <strong>pełny dostęp do wszystkich cen na portalu.</strong>
                </Text>

                <div className={propertyBoxStyle}>
                    {isLoading && (
                        <div className={loaderWrapperStyle}>
                            <Loader />
                        </div>
                    )}
                    {propertyData && (
                        <>
                            <Image src={getThumbnailLink(propertyData.offer.main_image_url, 440, 248)} alt="" width="496px" height="240px" />
                            <div className={propertyInfoStyle}>
                                {propertyData.price && propertyData.price_m2 && (
                                    <div className={priceRowStyle}>
                                        <Text variant="headline_3" as="p">
                                            <Highlight>
                                                <strong>{priceFormat(propertyData.price)}</strong>
                                            </Highlight>
                                        </Text>
                                        <Text variant="info_txt_1">
                                            <strong>{priceM2Format(propertyData.price_m2)}</strong>
                                        </Text>
                                    </div>
                                )}
                                <div className={regionRowStyle}>
                                    <MapMarkerLocationIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />
                                    <Text variant="info_txt_1">{propertyData.offer.region.short_name}</Text>
                                </div>
                                <PropertyInsightBox
                                    variant={variant}
                                    isLoading={isInsightLoading}
                                    percentageBelow={percentageBelow}
                                    promotionExample={promotionExample}
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className={buttonsWrapper}>
                    <Button
                        variant="filled_primary"
                        fullWidth
                        disabled={isDisabled}
                        isLoading={isSending}
                        onClick={handleConfirm}
                        className={buttonStyle}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.CONFIRM_LEAD_BUTTON}
                    >
                        {confirmButtonText}
                    </Button>
                    {renderSecondaryButton()}
                    <Button variant="none_secondary" size="x-small" fullWidth onClick={handleKeepBrowsing} className={css({minH: "40px"})}>
                        Tylko się rozglądam
                    </Button>
                </div>
            </Modal.Layout.EmptyResponsive>
        </Modal>
    );
};

const modalStyle = css({
    position: "relative",
    flexDirection: "column",
    pt: 1.5,
    px: 1.5,
    pb: 0,

    md: {
        pt: 3,
        px: 3,
        maxWidth: "544px"
    }
});

const propertyBoxStyle = css({
    borderRadius: 2,
    boxShadow: "2",
    my: 3,
    w: "100%",
    maxWidth: "496px",
    mx: "auto",

    "& img": {
        maxWidth: "100%"
    }
});

const loaderWrapperStyle = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    p: 3
});

const propertyInfoStyle = css({
    p: 2
});

const priceRowStyle = css({
    alignItems: "baseline",
    display: "flex",
    gap: 1.5
});

const regionRowStyle = css({
    alignItems: "center",
    display: "flex",
    gap: 1.5,
    mb: 1.5
});

const buttonsWrapper = css({
    w: "100%",
    position: "sticky",
    bottom: 0,
    left: 0,
    backgroundColor: "var(--colors-white)",
    p: 1.5,
    md: {
        px: 3
    }
});

const buttonStyle = css({
    mb: 2,
    minH: "56px"
});
